import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventIcon from "@mui/icons-material/Event";
import PaymentsIcon from "@mui/icons-material/Payments";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { sellRepository } from "../sell.repository";
import { Sell } from "../sell.entity";
import { formatBRL, paymentMethodLabel } from "../utils/sales-analytics";

export const SaleDetailPage = () => {
  application.useHasPermission([Permission.SELL, Permission.READ_SALES_ANALYTICS]);
  const moduleAccess = useModuleAccess();

  const { sellId } = useParams<{ sellId: string }>();
  const [sell, setSell] = useState<Sell | null | undefined>(undefined);

  const load = useCallback(async () => {
    if (!sellId) {
      setSell(null);
      return;
    }
    try {
      const s = await sellRepository.findById(sellId);
      setSell(s);
    } catch {
      setSell(null);
    }
  }, [sellId]);

  useEffect(() => {
    if (moduleAccess.loading || !moduleAccess.isEnabled("statistics_reports")) return;
    load();
  }, [load, moduleAccess.loading, moduleAccess.modules]);

  if (moduleAccess.loading) {
    return (
      <Shell title="Detalhes da venda">
        <LoadingOverlay message="Carregando módulo…" />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("statistics_reports")) {
    return (
      <Shell title="Detalhes da venda">
        <PageHeader title="Detalhes da venda" description="Consulta de venda concluída." />
        <ModuleLockedView
          moduleName="Estatísticas e relatórios"
          groupId="statistics_reports"
          details="Ative este módulo para acessar o detalhe das vendas a partir do painel de estatísticas."
          features={["Detalhamento de cada venda", "Histórico de transações", "Insights de performance"]}
        />
      </Shell>
    );
  }

  if (sell === undefined) {
    return (
      <Shell title="Detalhes da venda">
        <LoadingOverlay message="Carregando venda…" />
      </Shell>
    );
  }

  if (!sell) {
    return (
      <Shell title="Venda não encontrada">
        <PageHeader
          title="Venda não encontrada"
          description="Não foi possível localizar esta venda ou você não tem acesso."
        />
        <Button
          component={RouterLink}
          to="/vendas/estatisticas"
          startIcon={<ArrowBackIcon />}
          variant="contained"
        >
          Voltar ao painel
        </Button>
      </Shell>
    );
  }

  const total = sell.getSellTotal();
  const completed = sell.getCompletedAt();
  const lines = sell.getProducts().getValues();

  return (
    <Shell title="Detalhes da venda">
      <PageHeader
        title="Detalhes da venda"
        description={`Identificador ${sell.id}`}
        action={
          <Button
            component={RouterLink}
            to="/vendas/estatisticas"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Painel
          </Button>
        }
      />

      <Stack spacing={3}>
        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                icon={<ReceiptLongIcon />}
                label={paymentMethodLabel[sell.getPaymentMethod()]}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                icon={<PersonOutlineIcon />}
                label={sell.getCustomer()?.trim() || "Cliente não informado"}
                variant="outlined"
              />
              <Chip
                icon={<EventIcon />}
                label={`Início: ${sell.getStartedAt().format("DD/MM/YYYY HH:mm")}`}
                variant="outlined"
              />
              {completed ? (
                <Chip
                  icon={<PaymentsIcon />}
                  label={`Concluída: ${completed.format("DD/MM/YYYY HH:mm")}`}
                  variant="outlined"
                />
              ) : (
                <Chip label="Sem data de conclusão" variant="outlined" />
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={2}>
              <Typography variant="h6" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="h5" fontWeight={800} color="primary.main">
                {formatBRL(total)}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Itens
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Qtd.</TableCell>
                    <TableCell align="right">Unit.</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.entity.getName()}</TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">{formatBRL(line.price)}</TableCell>
                      <TableCell align="right">
                        {formatBRL(line.price * line.quantity)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {lines.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Nenhum item nesta venda.
              </Typography>
            ) : null}
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary">
          Registro interno para conferência e conferência de caixa.
        </Typography>
      </Stack>
    </Shell>
  );
};
